import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@trellixio/roaster-coffee';
import { TableSection, Pagination, Modal } from 'components';
import { useAPI, useDebounce, useGetUser } from 'hooks';
import { formatDate, formatNumber } from 'helpers';
import Locales from 'locales';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';

const DepositsPage = () => {
  const { isAgency } = useGetUser();
  const { api } = useAPI();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const debouncedSearchQuery = useDebounce(search, 1000);
  const queryClient = useQueryClient();
  const itemsPerPage = 5;
  const limit = 30;

  const fetchParams = search.length > 0 ? { q: debouncedSearchQuery } : { limit };

  const { data: deposits } = useQuery({
    queryFn: api.list('deposit/', fetchParams),
    queryKey: ['deposit/', debouncedSearchQuery, limit],
    keepPreviousData: true,
  });

  const isInvoiceOnlyDeposit = (invoiceCode) =>
    deposits.filter((deposit) => deposit.invoice_code === invoiceCode).length === 1;

  const totalPages = deposits ? Math.ceil(deposits.length / itemsPerPage) : 1;
  const startIndex = (page - 1) * itemsPerPage;
  const currentPageData = deposits ? deposits.slice(startIndex, startIndex + itemsPerPage) : [];

  const nextPage = () => setPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setPage((p) => Math.max(p - 1, 1));

  const statusVariant = (isInvoicePaid) =>
    isInvoicePaid ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300';

  const { mutate: deleteDeposit } = useMutation({
    mutationFn: (depositId) => api.remove('deposit', depositId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      queryClient.invalidateQueries({ queryKey: ['deposit/'] });
      queryClient.invalidateQueries({ queryKey: ['deposit/stats/default/'] });
      showToast({
        message: Locales.formatString(Locales.deposit.toast.delete_deposit),
        level: 'success',
      });
    },
  });

  const handleDelete = (deposit) => {
    setOpenDeleteModal(true);
    setSelectedItem(deposit);
  };

  return (
    <TableSection
      title="Dépôts"
      description="Liste des dépôts effectués par les pèlerins"
      actionLabel="Ajouter un dépôt"
      onSearch={(value) => setSearch(value.trim())}
      searchPlaceholder="Rechercher un dépôt..."
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date de dépôt</TableHead>
            <TableHead>Société</TableHead>
            <TableHead>Pèlerin</TableHead>
            <TableHead>Passeport</TableHead>
            <TableHead>Montant</TableHead>
            <TableHead>Facture</TableHead>
            <TableHead>Statut</TableHead>
            {isAgency && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>

        <TableBody>
          {currentPageData.map((deposit) => (
            <TableRow key={deposit.id}>
              <TableCell>{formatDate(deposit.created)}</TableCell>
              <TableCell>{deposit.agency_name}</TableCell>
              <TableCell>{deposit.pilgrim_name}</TableCell>
              <TableCell>{deposit.pilgrim_passport}</TableCell>
              <TableCell>{`${formatNumber(deposit.amount)} F CFA`}</TableCell>
              <TableCell>{deposit.invoice_code}</TableCell>
              <TableCell>
                <Badge variant="outline" className={`${statusVariant(deposit.is_invoice_paid)} rounded-full`}>
                  {deposit.is_invoice_paid ? 'Payé' : 'En attente'}
                </Badge>
              </TableCell>
              {isAgency && (
                <TableCell>
                  {!deposit.is_invoice_paid && (
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(deposit)}>
                      Supprimer
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {selectedItem && (
        <Modal
          isOpen={openDeleteModal}
          onOpenChange={setOpenDeleteModal}
          title={Locales.deposit.suppress_deposit}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpenDeleteModal(false)}>
                {Locales.base.button.discard}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  deleteDeposit(selectedItem.id);
                  setOpenDeleteModal(false);
                }}
              >
                {Locales.base.button.delete}
              </Button>
            </>
          }
        >
          <p>
            {Locales.formatString(Locales.deposit.delete_deposit, {
              amount: <strong>{formatNumber(selectedItem.amount)}</strong>,
              fullName: <strong>{selectedItem.pilgrim_name}</strong>,
            })}
            {isInvoiceOnlyDeposit(selectedItem.invoice_code) &&
              Locales.formatString(Locales.deposit.warning_last_deposit, {
                code: <strong>{selectedItem.invoice_code}</strong>,
              })}
          </p>
        </Modal>
      )}

      <Pagination page={page} totalPages={totalPages} onPrev={prevPage} onNext={nextPage} />
    </TableSection>
  );
};

export default DepositsPage;
